import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Loading Overlay Component
 * Shows a loading indicator over content
 * 
 * @param {boolean} isLoading - Whether to show the loading indicator
 * @param {string} message - Optional loading message
 * @param {string} type - Type of loading indicator: 'overlay' | 'inline' | 'fullscreen'
 */
const LoadingOverlay = ({ 
  isLoading, 
  message = 'Loading...', 
  type = 'overlay',
  className = ''
}) => {
  if (!isLoading) return null;

  // Fullscreen loading (for initial app load)
  if (type === 'fullscreen') {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
          <div className="text-center">
            <p className="text-lg font-medium text-gray-900">{message}</p>
            <p className="text-sm text-gray-500 mt-1">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  // Inline loading (for small sections)
  if (type === 'inline') {
    return (
      <div className={`flex items-center justify-center py-8 ${className}`}>
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
          <span className="text-sm text-gray-600">{message}</span>
        </div>
      </div>
    );
  }

  // Overlay loading (default - covers content)
  return (
    <div className={`absolute inset-0 bg-white bg-opacity-90 z-10 flex items-center justify-center rounded-lg ${className}`}>
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
        <span className="text-sm font-medium text-gray-700">{message}</span>
      </div>
    </div>
  );
};

/**
 * Loading Bar Component
 * Shows a progress bar at the top of the screen
 */
export const LoadingBar = ({ isLoading }) => {
  if (!isLoading) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-gray-200">
      <div className="h-full bg-blue-600 animate-loading-bar" />
    </div>
  );
};

/**
 * Loading Skeleton for cards
 */
export const SkeletonCard = ({ count = 1 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-3"></div>
          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
        </div>
      ))}
    </>
  );
};

/**
 * Loading Skeleton for table rows
 */
export const SkeletonTable = ({ rows = 5, columns = 4 }) => {
  return (
    <div className="animate-pulse">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="border-b border-gray-200 py-4 px-6">
          <div className="flex items-center gap-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <div key={colIndex} className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Loading Dots Animation
 */
export const LoadingDots = ({ className = '' }) => {
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
    </div>
  );
};

export default LoadingOverlay;
